// The ONE data_type → extract-kind mapping (C2).
//
// A ficha field says what a dato MEANS (`field_definition.data_type`, plus
// `options` when it is an enum); an `ask` node says how the Agente INTERPRETS
// the answer (`AskExtract`). Those are two vocabularies for the same fact, and
// until now nobody translated between them: the flow author typed a var name by
// hand and picked a kind by hand, so "Especialidad" — an enum with seven
// tenant-editable options — was authored as `{kind:'string'}` and captured
// VERBATIM (F39). A patient who answers "me duele el pecho" got "me duele el
// pecho" written into `leads.specialty_label`, which is what the board chip and
// the área filter render.
//
// WHY IT LIVES HERE, and why there is exactly one of it. Two consumers need the
// translation: the capture-flow GENERATOR (packages/api, C1) which authors asks
// from a process's ficha, and the flow EDITOR's field pickers (packages/web,
// C2) which set an ask's kind when the author picks a ficha field. Two copies of
// this table is the drift this cycle exists to end — so it is one file, on its
// own web-safe subpath (`@vysto/shared/bot-config/field-kind-map`), with one
// test both consumers' behaviour is asserted against.
//
// PURE + web-safe: no barrel import, no React, no DB. `AskExtract` is a
// type-only import from the skills contracts, so nothing is pulled into the
// browser bundle at runtime.

import type { AskExtract } from '../skills/types.js'

/** The `field_definition.data_type` vocabulary, as the database enforces it
 *  (`field_definition_data_type_check`, db/migrations/
 *  1748000047000_create-custom-fields.sql:36-37). Seven values, no more: this
 *  list is the CHECK constraint, not a wish. */
export const FICHA_DATA_TYPES = [
  'text',
  'number',
  'date',
  'boolean',
  'enum',
  'phone',
  'email',
] as const

export type FichaDataType = (typeof FICHA_DATA_TYPES)[number]

/** True for a value the catalog's CHECK constraint would accept. */
export function isFichaDataType(value: string): value is FichaDataType {
  return (FICHA_DATA_TYPES as readonly string[]).includes(value)
}

/** De dónde salen las opciones de una lista (D21-1), como la base lo exige
 *  (`field_definition_options_source_check`, db/migrations/
 *  1748000810000_field-definition-options-source.sql). Tres valores, no más:
 *
 *    lista      las escribe el cliente y viven en `options`
 *    agenda     las especialidades de sus médicos activos
 *    catalogo   los nombres de sus artículos activos
 *
 *  Vive aquí, junto al mapa de tipos, porque es la MISMA pregunta vista de
 *  lado: qué opciones tiene este dato. Y vive en `@vysto/shared` porque la
 *  consola y el API la necesitan los dos, y `@vysto/shared` no puede importar
 *  `@vysto/engine` (donde la lista se resuelve contra la base). */
export const FIELD_OPTIONS_SOURCES = ['lista', 'agenda', 'catalogo'] as const

export type FieldOptionsSource = (typeof FIELD_OPTIONS_SOURCES)[number]

/** The part of a ficha field definition this mapping reads. Structural on
 *  purpose: the API passes a `FieldDefinitionRow` straight from the catalog and
 *  the web passes the JSON it received, and neither should have to build an
 *  adapter to ask one question. */
export interface FichaFieldShape {
  data_type: string
  /** Only meaningful for `enum`. `null` on every other row (the column is
   *  nullable jsonb). */
  options?: readonly string[] | null
  /** De dónde SALEN esas opciones (D21-1). `lista`, ausente o `null` = las
   *  escribió el cliente. Viaja hasta el `extract` (D21-3) para que el runtime
   *  pueda volver a resolver la lista al contestar, en vez de quedarse con la
   *  que se horneó el día que se publicó. */
  options_source?: FieldOptionsSource | null | undefined
}

/**
 * The extract kind an ask node should use to capture this ficha field.
 *
 * Whole table, in the catalog's own order:
 *
 *   text     → string          (deterministic passthrough)
 *   number   → monetary_mxn    (the only numeric kind the runtime has; every
 *                               numeric lead preset today is money — presupuesto,
 *                               monto_buscado)
 *   date     → datetime_iso    (LLM-parsed, "el jueves" included)
 *   boolean  → boolean
 *   enum     → { kind: 'enum', values: options }   ← the F39 fix
 *   phone    → phone_mx
 *   email    → email
 *
 * ENUM IS THE POINT. `{kind:'enum', values}` sends the answer to the classifier
 * with a JSON schema whose `value` is `oneOf` the author's options or `null`
 * (packages/engine/src/skills/llm-extract.ts) — so an off-option paraphrase is
 * either classified INTO an option or fails and re-asks. It can never be stored
 * verbatim. Note the config key is `values`, not `options`: the catalog and the
 * runtime disagree on the word, which is precisely why this translation exists
 * in one place instead of at each call site.
 *
 * DEGRADED CASES, both deliberate:
 *   - An `enum` with no options (or an empty list) maps to `string`. An enum
 *     extract with zero allowed values is a schema nothing can satisfy: every
 *     answer would fail, burn the retry budget and hand off. A free-text capture
 *     is worse data but a working conversation, and the picker flags it. CON
 *     FUENTE (D21-3) la degradación es la MISMA, y se queda: significa "la
 *     fuente estaba vacía AL PUBLICAR". Que la lista se vacíe DESPUÉS de
 *     publicar es otro caso y lo atiende el runtime, que se queda con las
 *     opciones horneadas antes que mandar un `oneOf` vacío.
 *   - An unknown `data_type` maps to `string` rather than throwing. This runs
 *     inside an editor render; a catalog row from a future migration must not
 *     blank the inspector.
 */
export function askExtractForDataType(field: FichaFieldShape): AskExtract {
  switch (field.data_type) {
    case 'phone':
      return { kind: 'phone_mx' }
    case 'email':
      return { kind: 'email' }
    case 'date':
      return { kind: 'datetime_iso' }
    case 'number':
      return { kind: 'monetary_mxn' }
    case 'boolean':
      return { kind: 'boolean' }
    case 'enum': {
      const values = (field.options ?? []).filter((v) => typeof v === 'string' && v.trim() !== '')
      if (values.length === 0) return { kind: 'string' }
      // LA FUENTE VIAJA CON LA PREGUNTA (D21-3), y sólo cuando hay una: un dato
      // de lista escrita emite exactamente el mismo `extract` de siempre, así
      // que ni el flujo publicado ni la deriva de ningún espacio de hoy cambian
      // un byte.
      const source = field.options_source
      if (source !== undefined && source !== null && source !== 'lista') {
        return { kind: 'enum', values: [...values], source }
      }
      return { kind: 'enum', values: [...values] }
    }
    case 'text':
    default:
      return { kind: 'string' }
  }
}
